import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  ShieldCheck,
  CheckCircle,
  Database,
  Building,
  Check,
  FileSpreadsheet,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import RadialVerification from "../../Components/RadialVerification/RadialVerification";
import {
  VERIFICATION_HERO_STEPS,
  PROCEDURE_POINTS,
  VERIFICATION_SERVICES,
} from "../../Data/dataVerificationData";

// Reusing base validation styles to prevent duplicate CSS
import "../../Style/services/data-validation.css";
import "../../Style/services/data-verification.css";

const cardHover = {
  initial: { y: 0, opacity: 0.95 },
  hover: { y: -6, opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
};

export default function DataVerification() {
  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="validation-page ver-page">
      <Seo
        title="Increase Your Sales Pipeline with Verified Data | Conectify Tech"
        description="Strengthen your internal data and expand your sales pipeline with easy-to-convert leads verified at multiple levels by Conectify Tech data scientists."
      />

      {/* Hero Section */}
      <section className="val-hero">
        <Container className="val-hero__container">
          <motion.div
            className="val-hero__content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow">
              <Sparkles size={14} />
              <span>Multi-Level Human & AI Verification</span>
            </div>

            <h1 className="val-hero__title">
              Increase Your Sales Pipeline with{" "}
              <span className="text-gradient">Our Verified Data</span>
            </h1>

            <p className="val-hero__lead">
              Data Verification process plays an important role in data-driven marketing. It enables
              you to maintain a trusted and verified B2B contact list of your leads and prospects.
              Strengthen your internal data and expand your sales pipeline with easy-to-convert leads.
            </p>

            <p className="val-hero__sublead">
              Companies rely heavily on a verified database to ensure optimal reach with target
              audiences who intend to buy. Conectify Tech's B2B data verification process verifies
              data at multiple levels to maximize quality results. Our data scientists manually review
              the data to ensure the accuracy of phone numbers, email addresses, social information,
              and more. Enhance your business reach by reaching the right audience on a global scale.
            </p>

            {/* Hero Tags displaying Building alongside ShieldCheck and CheckCircle */}
            <div className="val-hero__tags">
              <span className="val-tag">
                <ShieldCheck size={13} className="text-primary" />
                <span>Multi-Tier Manual Reviews</span>
              </span>
              <span className="val-tag">
                <Building size={13} className="text-primary" />
                <span>Enterprise Firmographic Verification</span>
              </span>
              <span className="val-tag">
                <Database size={13} className="text-primary" />
                <span>Global Phone & Email Accuracy</span>
              </span>
              <span className="val-tag">
                <FileSpreadsheet size={13} className="text-success" />
                <span>Clean Excel & CRM Export</span>
              </span>
            </div>

            <div className="val-hero__actions">
              <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
                Verify My Leads Pipeline
              </Button>
              <Button variant="secondary" onClick={handleConsultation}>
                Request Sample Audit
              </Button>
            </div>
          </motion.div>

          {/* Interactive Stepper Board */}
          <motion.div
            className="val-hero__card-wrap"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <div className="val-board-card">
              <div className="val-board-card__header">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={20} className="text-primary" />
                  <span>Pipeline Acceleration</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-primary font-semibold">
                  <CheckCircle size={14} />
                  <span>Human-Verified</span>
                </div>
              </div>

              <div className="val-stepper-list">
                {VERIFICATION_HERO_STEPS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={item.title}
                      className="val-stepper-row"
                      variants={cardHover}
                      initial="initial"
                      whileHover="hover"
                    >
                      <div className="val-stepper-row__icon">
                        <Icon size={18} />
                      </div>
                      <div className="val-stepper-row__text">
                        <span className="step-tag">Phase {item.step}</span>
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <div className="val-board-card__badge">
                <Check size={14} className="text-success" />
                <span>Global Multi-Level Authenticity Guarantee</span>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Database Verification Procedures (With Radial Engine) */}
      <section className="ver-procedures">
        <Container>
          <div className="ver-procedures__box">
            <div className="ver-procedures__content">
              <div className="section-eyebrow">Interactive Lifecycle</div>
              <h2>Database Verification Procedures</h2>
              <p className="ver-procedures__lead">
                B2B data verification is an essential process for adding value to your marketing and
                sales database. Verify your lead data and increase your sales with B2B lead
                verification services. Our data scientists work efficiently to verify data alongside
                intelligence sources and to eliminate ineffective data.
              </p>

              <div className="ver-procedures__list">
                {PROCEDURE_POINTS.map((pt, index) => {
                  // Use FileSpreadsheet for the final "Authenticate & Update" stage
                  const Icon = index === 2 ? FileSpreadsheet : pt.icon;
                  return (
                    <div key={pt.title} className="ver-procedure-item">
                      <div className="ver-procedure-item__icon">
                        <Icon size={18} />
                      </div>
                      <div className="ver-procedure-item__text">
                        <h4>• {pt.title}</h4>
                        <p>{pt.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Interactive Radial Wheel */}
            <div className="ver-procedures__visual">
              <RadialVerification />
            </div>
          </div>
        </Container>
      </section>

      {/* Why Choose Data Verification? */}
      <section className="val-why">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Enterprise Validation ROI</span>
            <h2 className="section-title">Why Choose Data Verification?</h2>
            <p className="section-desc max-w-2xl mx-auto">
              More than 65,000+ companies reported the largest increase in revenue when choosing
              fact-checking services. With our various fact-checking solutions, we verify customer and
              business prospects that are deeply destined to buy and thus guarantee a high rate of
              return.
            </p>
          </div>

          <div className="ver-services__grid">
            {VERIFICATION_SERVICES.map((srv) => {
              const Icon = srv.icon;
              return (
                <motion.div
                  key={srv.title}
                  className="val-why-card ver-service-card"
                  variants={cardHover}
                  initial="initial"
                  whileHover="hover"
                >
                  <div className="val-why-card__icon">
                    <Icon size={22} />
                  </div>
                  <h4>{srv.title}</h4>
                  <p>{srv.desc}</p>
                  <div className="val-why-card__check">
                    <CheckCircle size={14} className="text-primary" />
                    <span>{srv.stat}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Bottom CTA */}
      <section className="b2b-cta">
        <Container>
          <div className="b2b-cta__card">
            <h2>Invest in Our Data Verification Services</h2>
            <p>
              Remove dead numbers, eliminate false emails, and maintain verified credibility with
              manual audit standards trusted by enterprise marketing teams.
            </p>
            <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
              Schedule a Database Verification 
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}